import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import '../styles/about.css';
import { resolveStaticImage } from '../lib/static-images';

const artistImage = 'https://www.figma.com/api/mcp/asset/acf6a7cd-9808-439b-8780-c39380e3436d';
const studioImage = 'https://www.figma.com/api/mcp/asset/6654d00c-0a58-4ac0-8c19-e74da8d76111';

const navItems = [
  ['Home', '/'], ['Services', '/services'], ['Portfolio', '/portfolio'],
  ['About', '/about'], ['Booking', '/book/service'], ['Contact', '/contact'],
] as const;

const standards = [
  ['01','Precision','Preparation, shaping and structure are handled carefully so the finished set feels balanced.'],
  ['02','Hygiene','Tools, surfaces and the service area follow clean, thoughtful working standards.'],
  ['03','Personalisation','Length, shape, colour and art are adjusted to the client’s taste, routine and maintenance needs.'],
  ['04','Longevity','Product, structure and aftercare are selected with wear and future maintenance in mind.'],
];

const expectations = [
  ['01','Before the appointment','Choose the service, share inspiration, confirm the available time and review the stated deposit.'],
  ['02','During the service','Expect consultation, careful preparation, focused detail and clear communication throughout.'],
  ['03','After the service','Receive aftercare guidance, settle the remaining balance and use WhatsApp for support or future maintenance.'],
];

function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header className="site-header">
      <Link to="/" className="brand"><strong>MF_NAILEDIT</strong><span>NAIL ARTIST</span></Link>
      <nav className="desktop-nav" aria-label="Primary navigation">
        {navItems.map(([label, path]) => <NavLink key={path} to={path}>{label}</NavLink>)}
      </nav>
      <Link to="/book/service" className="button button-primary header-cta">Book appointment</Link>
      <button className="menu-button" type="button" aria-expanded={open} aria-controls="about-mobile-menu" onClick={() => setOpen((value) => !value)}>
        {open ? 'Close' : 'Menu'}
      </button>
      {open && (
        <nav id="about-mobile-menu" className="mobile-nav" aria-label="Mobile navigation">
          {navItems.map(([label, path]) => <NavLink key={path} to={path} onClick={() => setOpen(false)}>{label}</NavLink>)}
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="footer-brand"><h2>MF_NAILEDIT</h2><p>Luxury nail artistry and pedicure appointments in Akure, Ondo State.</p><p className="accent-text">@mf_nailedit • WhatsApp booking support</p></div>
      <div className="footer-links">
        <div><h3>EXPLORE</h3><Link to="/services">Services</Link><Link to="/portfolio">Portfolio</Link><Link to="/about">About</Link></div>
        <div><h3>BOOKING</h3><Link to="/book/service">Book appointment</Link><Link to="/services">Policies</Link><Link to="/contact">Contact</Link></div>
        <div><h3>STUDIO</h3><span>Akure, Ondo State</span><span>Appointment only</span><span>Instagram</span></div>
      </div>
    </footer>
  );
}

export default function About() {
  return (
    <main>
      <Header />
      <section className="about-hero section-shell">
        <div className="about-hero-copy">
          <p className="eyebrow">ABOUT MF_NAILEDIT • AKURE, ONDO STATE</p>
          <h1>Precision, artistry and a calm luxury experience.</h1>
          <p className="lead">MF_NAILEDIT is a focused nail artistry and pedicure studio built around careful preparation, clean finishing and designs that feel personal—not mass-produced.</p>
          <div className="studio-positioning"><strong>APPOINTMENT ONLY</strong><span>Akure, Ondo State • Nail artistry and luxury pedicure care</span></div>
          <div className="hero-actions"><Link className="button button-primary" to="/book/service">Book appointment</Link><Link className="button button-secondary" to="/portfolio">View portfolio</Link></div>
        </div>
        <div className="about-image"><img src={resolveStaticImage(artistImage)} alt="MF_NAILEDIT artist and studio branding" loading="eager" decoding="async" fetchPriority="high" /></div>
      </section>
      <section className="artist-story">
        <div className="section-shell story-grid">
          <div className="story-copy">
            <p className="eyebrow">THE ARTIST BEHIND THE DETAIL</p>
            <h2>Every appointment begins with listening.</h2>
            <p className="lead">Shape, lifestyle, inspiration and maintenance needs guide the service. The goal is polished work that feels expressive, practical to wear and considered from preparation to aftercare.</p>
            <div className="principles">
              <div><span>01</span><div><strong>Listen first</strong><p>The service starts with your preferences, routine and inspiration.</p></div></div>
              <div><span>02</span><div><strong>Design with intention</strong><p>Shape, length, colour and detail are chosen as one complete look.</p></div></div>
              <div><span>03</span><div><strong>Finish for wear</strong><p>Structure and aftercare are considered—not only the first-day photograph.</p></div></div>
            </div>
            <Link className="button button-secondary" to="/services">View services</Link>
          </div>
          <div className="workspace-image"><img src={resolveStaticImage(studioImage)} alt="MF_NAILEDIT studio workspace and nail work" loading="lazy" decoding="async" /></div>
        </div>
      </section>
      <section className="content-section section-shell">
        <div className="section-heading"><p className="eyebrow">THE STUDIO STANDARD</p><h2>The details clients may not see still matter.</h2><p className="lead">The experience is shaped by preparation, cleanliness, personal choices and finishes planned for real wear.</p></div>
        <div className="about-standards-grid">{standards.map(([n,t,b],i)=><article key={t} className={`experience-card ${i===1||i===2?'featured':''}`}><span>{n}</span><h3>{t}</h3><p>{b}</p><small>STUDIO STANDARD</small></article>)}</div>
      </section>
      <section className="appointment-experience">
        <div className="section-shell">
          <div className="section-heading"><p className="eyebrow">WHAT CLIENTS CAN EXPECT</p><h2>Considered from the first message to the finished set.</h2><p className="lead">The experience should remain clear before, during and after the appointment—not only when the final photograph is taken.</p></div>
          <div className="expectation-grid">{expectations.map(([n,t,b])=><article key={t}><span>{n}</span><h3>{t}</h3><p>{b}</p></article>)}</div>
        </div>
      </section>
      <section className="final-cta-section"><div className="booking-cta booking-cta-dark"><h2>Ready for an appointment that feels considered?</h2><p>Choose a service, add your inspiration and secure the time with the stated deposit.</p><Link className="button button-secondary" to="/book/service">Start booking</Link></div></section>
      <Footer />
    </main>
  );
}
